import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";

const SHIFT_LABELS = { mattino: "Mattino", pomeriggio: "Pomeriggio", notte: "Notte" };

const PendingChecklistGate = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [savingId, setSavingId] = useState(null);

  const isFrontOffice = ["front_office", "night_audit", "management"].includes(user?.department) && user?.role !== "super_admin";

  useEffect(() => {
    if (!user || !isFrontOffice) { setLoading(false); return; }
    const load = async () => {
      try {
        const res = await axios.get(`${API}/checklists/pending-justifications`);
        setPending(res.data.pending || []);
      } catch {
        setPending([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, isFrontOffice]);

  if (loading || !isFrontOffice || pending.length === 0) return null;

  const current = pending[0];

  const setNote = (itemId, value) => setNotes((p) => ({ ...p, [itemId]: value }));

  const handleSubmit = async () => {
    const missing = current.items.filter((it) => !((notes[it.id] || "").trim()));
    if (missing.length > 0) {
      toast.error("Inserisci una motivazione per ogni voce non completata");
      return;
    }
    setSavingId(current.id);
    try {
      for (const it of current.items) {
        await axios.patch(`${API}/checklists/instance/${current.id}/item/${it.id}`, { note: notes[it.id].trim() });
      }
      await axios.post(`${API}/checklists/instance/${current.id}/close`);
      toast.success("Turno giustificato e chiuso");
      setPending((p) => p.slice(1));
      setNotes({});
    } catch {
      toast.error("Errore nel salvataggio delle motivazioni");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-stone-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="pending-justification-gate">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-stone-100">
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-wide">Azione richiesta</span>
          </div>
          <h2 className="font-outfit text-xl font-bold text-stone-900">Motiva le voci non completate</h2>
          <p className="text-sm text-stone-500 mt-1">
            Hai lasciato aperto il turno <strong>{SHIFT_LABELS[current.shift] || current.shift}</strong> del{" "}
            <strong>{current.date}</strong>. Inserisci una motivazione per ogni voce non spuntata per poter continuare.
          </p>
          {pending.length > 1 && (
            <p className="text-xs text-amber-600 mt-2">Turni da giustificare rimanenti: {pending.length}</p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {current.items.map((it) => (
            <div key={it.id} className="border border-stone-200 rounded-xl p-3" data-testid={`gate-item-${it.id}`}>
              <p className="text-sm text-stone-800 mb-2">{it.text}</p>
              <input
                type="text"
                value={notes[it.id] || ""}
                onChange={(e) => setNote(it.id, e.target.value)}
                placeholder="Motivazione (obbligatoria)..."
                className="w-full text-sm border border-stone-200 rounded-md px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
                data-testid={`gate-note-${it.id}`}
              />
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-stone-100">
          <Button
            onClick={handleSubmit}
            disabled={savingId === current.id}
            className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-xl h-12"
            data-testid="gate-submit-button"
          >
            <Lock className="w-4 h-4 mr-2" />
            {savingId === current.id ? "Salvataggio..." : "Salva motivazioni e chiudi turno"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PendingChecklistGate;
